<template>
  <div class="studio-panel" style="max-width: 700px;">

    <!-- Sub-tab strip — hidden while Dashboard is the only tab. -->
    <div class="sub-tabs" v-if="subTabs.length > 1">
      <button
        v-for="t in subTabs" :key="t.key"
        class="sub-tab"
        :class="{ active: activeSub === t.key }"
        @click="activeSub = t.key"
      >
        {{ t.label }}
        <span v-if="t.key === 'update' && skillsWithDrift.length"
              class="sub-tab-badge">{{ skillsWithDrift.length }}</span>
      </button>
    </div>

    <!-- ── Dashboard ────────────────────────────────────────── -->
    <div v-if="activeSub === 'dashboard'">
      <div class="reader-hero">
        <img v-if="store.reader?.background_image" :src="store.reader.background_image" class="hero-avatar" />
        <div class="hero-info">
          <h2 class="hero-name">{{ store.reader?.name }}</h2>
          <div class="hero-sub">{{ store.reader?.sub_domain }}.mysticaida.ai</div>
          <div class="hero-meta">
            <span class="live-status" :class="{ live: isLive }">
              {{ isLive ? '● Live' : '○ Draft' }}
            </span>
            <!-- Rank is meaningless until this Mystic has delivered a
                 reading. Gating on total_readers alone counted how many
                 Mystics EXIST, so a Draft with zero readings announced
                 "Rank #1 of 9" (funnel audit, 2026-09-03). -->
            <span v-if="rank && rank.total_readers && readerStats?.reading_count" class="rank-chip">
              ✦ Rank #{{ rank.position }} of {{ rank.total_readers }}
            </span>
          </div>
        </div>
      </div>

      <!-- Reading stats: seekers' response to your readings -->
      <div class="studio-card" v-if="readerStats" style="margin-bottom: 20px;">
        <h3 class="section-title">Your Readings</h3>
        <p class="section-sub">How seekers are responding to {{ store.reader?.name }}.</p>
        <div class="stats-grid">
          <div class="stat-tile" :title="`Total readings ${store.reader?.name} has delivered`">
            <div class="stat-num">{{ readerStats.reading_count || 0 }}</div>
            <div class="stat-lbl">Readings</div>
          </div>
          <div class="stat-tile" :title="`Unique seekers who have visited ${store.reader?.name}`">
            <div class="stat-num">{{ readerStats.querent_count || 0 }}</div>
            <div class="stat-lbl">Unique Seekers</div>
          </div>
          <div class="stat-tile" title="Seekers following your Mystic in the community">
            <div class="stat-num">{{ followersCount }}</div>
            <div class="stat-lbl">Followers</div>
          </div>
          <div class="stat-tile" title="Readings seekers chose to share publicly">
            <div class="stat-num">{{ readerStats.shared_count || 0 }}</div>
            <div class="stat-lbl">Shared</div>
          </div>
          <div class="stat-tile"
               :title="readerStats.total_ratings ? `Average of ${readerStats.total_ratings} seeker ratings` : 'No ratings yet'">
            <div class="stat-num">
              <template v-if="readerStats.total_ratings">🔮 {{ readerStats.average_rating }}</template>
              <template v-else> - </template>
            </div>
            <div class="stat-lbl">
              Avg Rating<template v-if="readerStats.total_ratings"> ({{ readerStats.total_ratings }})</template>
            </div>
          </div>
        </div>
      </div>

      <!-- Found in search (lesson-redo DESIGN.md G): how seekers find this Mystic. -->
      <FoundInSearch v-if="isLive" @navigate="$emit('navigate', $event)" />

      <!-- Your front door (mystic-page 2026-10-29): the address, Copy link, the
           QR and the counts; then Your daily drop with the horoscope link. -->
      <StudioFrontDoor v-if="isLive" :sub="store.reader?.sub_domain || ''" :portrait="store.reader?.avatar_image || store.reader?.background_image || ''" />

      <!-- Latest Mystic Drop preview -->
      <div class="studio-card" v-if="todayDrop && isLive" style="margin-bottom: 20px;">
        <h3 class="section-title">Latest Mystic Drop</h3>
        <p class="section-sub">
          {{ todayDrop.interval_label || 'Today' }}<template v-if="dropDateLabel"> · {{ dropDateLabel }}</template>
        </p>
        <a class="drop-card" :href="horoscopeUrl" target="_blank" rel="noopener">
          <div
            v-if="todayDrop.preview_image_url || todayDrop.drop_image_url || todayDrop.tarot_image_url"
            class="drop-img"
            :style="{ backgroundImage: `url(${todayDrop.preview_image_url || todayDrop.drop_image_url || todayDrop.tarot_image_url})` }"
          ></div>
          <div class="drop-body">
            <div class="drop-title">{{ todayDrop.title }}</div>
            <div class="drop-teaser" v-if="todayDrop.teaser">{{ todayDrop.teaser }}</div>
            <div class="drop-cta">View on site ↗</div>
          </div>
        </a>
      </div>
      <div class="studio-card" v-else-if="readerDetail && isLive" style="margin-bottom: 20px;">
        <h3 class="section-title">Latest Mystic Drop</h3>
        <p class="section-sub">No drop published yet - configure a drop schedule to start publishing horoscopes for your seekers.</p>
      </div>

      <!-- Recent shared readings (real engagement) -->
      <div class="studio-card" v-if="sharedReadings.length" style="margin-bottom: 20px;">
        <h3 class="section-title">Recent Shared Readings</h3>
        <p class="section-sub">Readings seekers chose to share publicly.</p>
        <div class="shared-list">
          <div v-for="(sr, i) in sharedReadings" :key="i" class="shared-row">
            <img v-if="sr.image_url" :src="sr.image_url" class="shared-thumb" alt="" />
            <div v-else class="shared-thumb shared-thumb-empty">🔮</div>
            <div class="shared-info">
              <div class="shared-title">{{ sr.summary_title || 'Untitled reading' }}</div>
              <div class="shared-meta">
                <span v-if="sr.querent_nick_name">{{ sr.querent_nick_name }}</span>
                <span>{{ sr.views || 0 }} views</span>
                <span v-if="sr.rating_count">🔮 {{ sr.average_rating }} ({{ sr.rating_count }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No "Go live" step: a Mystic is live from its birth. Unlisting is a
           privacy choice on the Mystic's own page, not a launch. -->
    </div>

    <!-- ── Update ──────────────────────────────────────────── -->
    <div v-else-if="activeSub === 'update'">
      <div class="studio-card">
        <h3 class="section-title">Skill upgrades</h3>
        <p class="section-sub">
          When a skill's authored prompts get a platform update, the
          new version surfaces here. Adopt to take the new prompts,
          or keep yours and lock in the upgrade.
        </p>

        <div v-if="loadingDiffs" class="loading">Checking for updates…</div>

        <div v-else-if="!skillsWithDrift.length" class="all-current">
          ✓ All your skills are on the latest version.
        </div>

        <div v-else class="upgrade-list">
          <div v-for="skill in skillsWithDrift" :key="skill.skill_key"
               class="seed-banner">
            <span class="seed-banner-bell">✦</span>
            <span class="seed-banner-text">
              <strong>{{ skill.display_name }}</strong> - upgrade to latest
              version (<strong>{{ diffsBySkill[skill.skill_key].platform_version }}</strong>).
            </span>
            <button class="seed-banner-action"
                    @click="diffOpenForSkill = skill.skill_key">
              Review changes
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Seed-version diff modal (per-skill). -->
    <div v-if="diffOpenForSkill && diffsBySkill[diffOpenForSkill]"
         class="seed-modal-overlay" @click.self="closeDiff">
      <div class="seed-modal">
        <header class="seed-modal-head">
          <h3>
            Upgrade {{ skillDisplayName(diffOpenForSkill) }} to
            {{ diffsBySkill[diffOpenForSkill].platform_version }}
          </h3>
          <button class="seed-modal-close" @click="closeDiff">✕</button>
        </header>
        <p class="seed-modal-sub">
          For each prompt that changed, choose Adopt to take the new
          version or Keep mine to preserve what you've authored.
          Either way locks in the upgrade so this prompt won't be
          flagged again.
        </p>

        <div class="seed-modal-body">
          <div v-for="g in diffsBySkill[diffOpenForSkill].diffs"
               :key="g.instruction_name" class="diff-group">
            <h4 class="diff-group-title">
              {{ g.instruction_name }}
              <span class="diff-group-type">{{ g.conversation_type }}</span>
            </h4>
            <div v-for="s in g.steps"
                 :key="`${g.instruction_name}::${s.step_name}`"
                 class="diff-step" :class="`kind-${s.kind}`">
              <div class="diff-step-head">
                <span class="diff-step-name">{{ s.step_name }}</span>
                <span class="diff-kind-pill">{{ s.kind }}</span>
              </div>
              <div v-if="s.kind === 'changed'" class="diff-bodies">
                <div class="diff-current">
                  <div class="diff-label">Your version</div>
                  <pre>{{ s.current.prompt || '(empty)' }}</pre>
                </div>
                <div class="diff-incoming">
                  <div class="diff-label">Incoming seed</div>
                  <pre>{{ s.incoming.prompt || '(empty)' }}</pre>
                </div>
              </div>
              <div v-else-if="s.kind === 'added'" class="diff-bodies">
                <div class="diff-incoming">
                  <div class="diff-label">Brand new (no row yet)</div>
                  <pre>{{ s.incoming.prompt || '(empty)' }}</pre>
                </div>
              </div>
              <div v-else-if="s.kind === 'removed'" class="diff-bodies">
                <div class="diff-current">
                  <div class="diff-label">Your row (no longer in seed)</div>
                  <pre>{{ s.current.prompt || '(empty)' }}</pre>
                </div>
              </div>
              <div v-if="s.kind === 'changed' && s.current" class="diff-actions">
                <button class="diff-btn diff-btn-adopt"
                        :class="{ active: decisionMap[s.current.step_id] === 'adopt' }"
                        @click="setDecision(s.current.step_id, 'adopt')">
                  Adopt
                </button>
                <button class="diff-btn diff-btn-keep"
                        :class="{ active: decisionMap[s.current.step_id] === 'keep' }"
                        @click="setDecision(s.current.step_id, 'keep')">
                  Keep mine
                </button>
              </div>
            </div>
          </div>
        </div>

        <footer class="seed-modal-foot">
          <span class="seed-modal-summary">
            {{ decisionCount }} of {{ changedCount }} reviewed
          </span>
          <button class="seed-modal-cancel" @click="closeDiff">Close</button>
          <button class="seed-modal-apply"
                  :disabled="!decisionCount || applying"
                  @click="applyDecisions">
            {{ applying ? 'Applying…' : 'Apply decisions' }}
          </button>
        </footer>
      </div>
    </div>

    <p v-if="error" class="error-message">{{ error }}</p>

  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { store } from '@/store'
import {
  updateReader,
  listSkillVariants,
  listReadingSessionSkills,
  getSeedVersionDiff,
  adoptSeedVersion,
  getReaderDetail,
} from '@/api-service'
import { parseAxiosError } from '@/utils/general-utils'
import FoundInSearch from '@/components/studio/FoundInSearch.vue'
import StudioFrontDoor from '@/components/studio/StudioFrontDoor.vue'

defineEmits(['navigate'])

const isLive = computed(() => store.reader && !store.reader.not_listed_in_community)

// Full public detail for the dashboard — reuses the profile aggregation so
// every metric the public profile shows (seekers, followers, rank, drop
// streak, today's drop, recent shared readings) is available here too.
const readerDetail = ref(null)
const readerStats = computed(() => readerDetail.value?.stats || null)
const rank = computed(() => readerDetail.value?.rank || null)
const followersCount = computed(() => readerDetail.value?.followers_count || 0)
const todayDrop = computed(() => readerDetail.value?.today_drop || null)
const sharedReadings = computed(() => readerDetail.value?.shared_readings || [])

async function loadReaderDetail() {
  const sub = store.reader?.sub_domain
  if (!sub) return
  try {
    readerDetail.value = await getReaderDetail(sub)
  } catch (e) { /* non-blocking — dashboard still renders without stats */ }
}

// Public surfaces this Mystic touches. The reader page lives on its own
// subdomain; horoscopes + Mystic Drops render on the marketing site keyed by
// sub_domain slug; the directory is that site's community carousel. Apex
// domain matches the Go-Live share link convention below.
const PUBLIC_DOMAIN = 'mysticaida.ai'
const horoscopeUrl = computed(() => `https://${PUBLIC_DOMAIN}/horoscope/${store.reader?.sub_domain}`)

const dropDateLabel = computed(() => {
  const iso = todayDrop.value?.drop_date
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d)) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
})

// ── Sub-tabs ───────────────────────────────────────────────────────
// Update (seed-version upgrade) tab axed — platform prompt updates are a lower
// layer the Mystic isn't bothered with. Single tab left; strip hides itself.
const subTabs = [
  { key: 'dashboard', label: 'Dashboard' },
]
const activeSub = ref('dashboard')

// ── Seed-version diff state (Update tab) ──────────────────────────
// Per-skill diff results, keyed by skill_key. ``skills`` is the list
// of active skills (from listReadingSessionSkills). ``diffOpenForSkill``
// is the skill_key currently being reviewed in the modal, or null.
const skills = ref([])
const diffsBySkill = reactive({})
const loadingDiffs = ref(true)
const diffOpenForSkill = ref(null)
const decisionMap = ref({})
const applying = ref(false)
const error = ref('')

const skillsWithDrift = computed(() =>
  skills.value.filter(
    s => diffsBySkill[s.skill_key] && !diffsBySkill[s.skill_key].is_current,
  )
)

const currentDiff = computed(() =>
  diffOpenForSkill.value ? diffsBySkill[diffOpenForSkill.value] : null
)

const changedCount = computed(() => {
  if (!currentDiff.value) return 0
  let n = 0
  for (const g of currentDiff.value.diffs || []) {
    for (const s of g.steps || []) {
      if (s.kind === 'changed') n += 1
    }
  }
  return n
})
const decisionCount = computed(() => Object.keys(decisionMap.value).length)

function skillDisplayName(key) {
  const s = skills.value.find(x => x.skill_key === key)
  return s ? s.display_name : key
}

function setDecision(stepId, decision) {
  decisionMap.value = { ...decisionMap.value, [stepId]: decision }
}

function closeDiff() {
  diffOpenForSkill.value = null
  decisionMap.value = {}
}

async function loadAllDiffs() {
  loadingDiffs.value = true
  try {
    const skillsResp = await listReadingSessionSkills()
    skills.value = skillsResp.skills || []
    // Pull each diff in parallel; tolerate per-skill failures so one
    // broken endpoint doesn't blank the whole tab.
    const results = await Promise.all(
      skills.value.map(s =>
        getSeedVersionDiff(s.skill_key)
          .then(d => [s.skill_key, d])
          .catch(() => [s.skill_key, null])
      )
    )
    for (const [key, d] of results) {
      if (d) diffsBySkill[key] = d
    }
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    loadingDiffs.value = false
  }
}

async function applyDecisions() {
  if (!diffOpenForSkill.value) return
  const decisions = Object.entries(decisionMap.value).map(
    ([step_id, dec]) => ({ step_id: Number(step_id), decision: dec }),
  )
  if (!decisions.length) return
  applying.value = true
  try {
    await adoptSeedVersion(diffOpenForSkill.value, decisions)
    // Reload only this skill's diff so the banner clears.
    const fresh = await getSeedVersionDiff(diffOpenForSkill.value)
    diffsBySkill[diffOpenForSkill.value] = fresh
    closeDiff()
  } catch (e) {
    error.value = parseAxiosError(e)
  } finally {
    applying.value = false
  }
}

// Hydrate skill variants on mount so checklist + Go Live gating reflect reality.
onMounted(async () => {
  if (!store.readerSkillVariants?.length) {
    store.readerSkillVariants = await listSkillVariants()
  }
  // Seed-version drift check removed with the Update tab — no longer surfaced
  // to the Mystic, so we don't run the per-skill diff calls on load.
  loadReaderDetail()
})


</script>

<style scoped>
@import '@/styles/studio-forms.css';

.go-live-error {
  margin-top: 10px;
  color: rgba(244,67,54,0.95);
  font-size: 13px;
  background: rgba(244,67,54,0.1);
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(244,67,54,0.3);
}
.reader-hero { display: flex; gap: 16px; align-items: center; margin-bottom: 24px; padding: 20px; background: rgba(255,255,255,0.04); border-radius: 12px; border: 1px solid rgba(255,255,255,0.08); }
.hero-avatar { width: 72px; height: 72px; border-radius: 10px; object-fit: cover; border: 1px solid rgba(255,255,255,0.1); }
.hero-info { flex: 1; }
.hero-name { font-size: 22px; font-weight: 600; color: #FFDFA3; margin: 0 0 4px; font-family: 'Cinzel', serif; background: none; border: none; padding: 0; box-shadow: none; }
.hero-sub { font-size: 13px; color: rgba(195,169,255,0.5); margin-bottom: 6px; }
.live-status { font-size: 12px; font-weight: 600; color: rgba(195,169,255,0.4); }
.live-status.live { color: #7DE8CC; }

.section-title { font-size: 14px; font-weight: 600; color: #C9A94E; margin: 0 0 4px; font-family: 'Cinzel', serif; }
.section-sub { font-size: 12px; color: rgba(195,169,255,0.4); margin-bottom: 16px; }
.checklist-items { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }

.go-live-btn { width: 100%; padding: 14px; background: rgba(201,169,78,0.2); border: 1px solid rgba(201,169,78,0.5); border-radius: 10px; color: #C9A94E; font-size: 15px; font-weight: 600; cursor: pointer; font-family: 'Cinzel', serif; transition: background 0.2s; }
.go-live-btn:hover:not(:disabled) { background: rgba(201,169,78,0.3); }
.go-live-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.live-share { margin-top: 12px; font-size: 13px; color: rgba(195,169,255,0.5); display: flex; gap: 8px; align-items: center; }
.live-share a { color: #C9A94E; font-weight: 600; text-decoration: none; }
.live-share a:hover { text-decoration: underline; }

/* ── Sub-tabs ─────────────────────────────────────────────── */
.sub-tabs {
  display: flex;
  gap: 2px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  border-bottom: 1px dashed rgba(255,255,255,0.08);
}
.sub-tab {
  padding: 8px 14px;
  background: transparent;
  border: none;
  color: rgba(195,169,255,0.55);
  font-size: 12px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sub-tab.active {
  color: #E8D8FF;
  border-bottom-color: rgba(201, 169, 78, 0.7);
}
.sub-tab-badge {
  display: inline-block;
  min-width: 18px;
  padding: 2px 6px;
  background: rgba(201,169,78,0.85);
  color: #1a1422;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  text-align: center;
}

.loading {
  padding: 18px 0;
  color: rgba(195, 169, 255, 0.6);
  font-style: italic;
  font-size: 13px;
}
.all-current {
  padding: 14px 0;
  color: rgba(125, 232, 204, 0.85);
  font-size: 13px;
}

/* ── Seed-version banner (per skill) ──────────────────────── */
.upgrade-list { display: flex; flex-direction: column; gap: 10px; }
.seed-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: rgba(201, 169, 78, 0.10);
  border: 1px solid rgba(201, 169, 78, 0.45);
  border-radius: 8px;
  color: #E8D8FF;
  font-size: 13px;
}
.seed-banner-bell { color: #C9A94E; font-size: 16px; }
.seed-banner-text { flex: 1; }
.seed-banner-action {
  background: rgba(201, 169, 78, 0.85);
  border: none;
  color: #1a1422;
  font-weight: 600;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.seed-banner-action:hover { background: #E0BD63; }

/* ── Diff modal ───────────────────────────────────────────── */
.seed-modal-overlay {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
}
.seed-modal {
  background: #14101c;
  border: 1px solid rgba(195,169,255,0.25);
  border-radius: 12px;
  width: min(900px, 100%);
  max-height: 90vh;
  display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6);
}
.seed-modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.seed-modal-head h3 {
  margin: 0; font-family: 'Cinzel', serif; color: #C9A94E;
  font-size: 16px;
}
.seed-modal-close {
  background: transparent; border: none; cursor: pointer;
  color: rgba(195,169,255,0.6); font-size: 18px;
}
.seed-modal-sub {
  padding: 12px 18px 0;
  font-size: 12px;
  color: rgba(195,169,255,0.7);
  font-style: italic;
}
.seed-modal-body {
  flex: 1; overflow-y: auto;
  padding: 12px 18px;
}
.diff-group { margin-bottom: 18px; }
.diff-group-title {
  font-family: 'Cinzel', serif; color: #E8D8FF; font-size: 13px;
  margin: 0 0 8px; padding-bottom: 4px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.diff-group-type {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px; color: rgba(195,169,255,0.4);
  margin-left: 6px;
}
.diff-step {
  padding: 10px 12px;
  margin-bottom: 8px;
  background: rgba(255,255,255,0.025);
  border-left: 3px solid rgba(255,255,255,0.1);
  border-radius: 4px;
}
.diff-step.kind-changed   { border-left-color: #C9A94E; }
.diff-step.kind-added     { border-left-color: #34d399; }
.diff-step.kind-removed   { border-left-color: #f87171; }
.diff-step.kind-unchanged { display: none; }
.diff-step-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 6px;
}
.diff-step-name {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px; color: #E8D8FF;
}
.diff-kind-pill {
  font-size: 10px;
  padding: 2px 7px;
  border-radius: 10px;
  background: rgba(195,169,255,0.12);
  color: #C9A94E;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.diff-bodies {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 8px;
}
.diff-bodies > div { background: rgba(0,0,0,0.25); padding: 8px 10px; border-radius: 4px; }
.diff-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;
  color: rgba(195,169,255,0.5);
  margin-bottom: 4px;
}
.diff-bodies pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: rgba(232,216,255,0.85);
  max-height: 160px;
  overflow-y: auto;
}
.diff-actions {
  display: flex; gap: 8px; justify-content: flex-end;
}
.diff-btn {
  background: transparent;
  border: 1px solid rgba(195,169,255,0.25);
  color: rgba(232,216,255,0.7);
  font-size: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.diff-btn.active.diff-btn-adopt {
  background: rgba(201,169,78,0.7); color: #1a1422; border-color: #C9A94E;
}
.diff-btn.active.diff-btn-keep {
  background: rgba(195,169,255,0.35); color: #1a1422; border-color: rgba(195,169,255,0.6);
}
.seed-modal-foot {
  padding: 12px 18px;
  border-top: 1px solid rgba(255,255,255,0.08);
  display: flex; align-items: center; gap: 10px;
}
.seed-modal-summary {
  font-size: 11px; color: rgba(195,169,255,0.5); flex: 1;
}
.seed-modal-cancel, .seed-modal-apply {
  border-radius: 4px;
  font-size: 13px;
  padding: 6px 14px;
  cursor: pointer;
  border: 1px solid rgba(195,169,255,0.2);
  background: transparent;
  color: rgba(232,216,255,0.8);
}
.seed-modal-apply {
  background: rgba(201,169,78,0.85);
  color: #1a1422;
  border-color: #C9A94E;
  font-weight: 600;
}
.seed-modal-apply:disabled { opacity: 0.4; cursor: not-allowed; }

.error-message {
  color: rgba(244, 67, 54, 0.9);
  font-size: 13px;
  margin-top: 12px;
}

/* Hero meta row (live status + community rank). */
.hero-meta { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.rank-chip {
  font-size: 11px; font-weight: 600; color: #C9A94E;
  background: rgba(201,169,78,0.12); border: 1px solid rgba(201,169,78,0.35);
  padding: 3px 9px; border-radius: 999px;
}

/* Reading stats grid (readings/seekers/followers/shared/rating/streak). */
.stats-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(96px, 1fr)); gap: 12px;
}
.stat-tile {
  text-align: center; padding: 16px 10px; border-radius: 12px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
}
.stat-num { font-family: 'Cinzel', serif; font-size: 22px; font-weight: 600; color: #C9A94E; }
.stat-lbl { font-size: 11px; color: rgba(195,169,255,0.55); margin-top: 4px; }

/* "Where this Mystic appears" deep-link rows. */
.touch-list { display: flex; flex-direction: column; gap: 8px; }
.touch-link {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px; border-radius: 10px; text-decoration: none;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  transition: border-color 0.2s, background-color 0.2s;
}
.touch-link:hover { border-color: rgba(201,169,78,0.5); background: rgba(255,255,255,0.06); }
.touch-ico { font-size: 18px; line-height: 1; }
.touch-body { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.touch-title { font-size: 14px; font-weight: 600; color: #E8D8FF; }
.touch-sub { font-size: 11px; color: rgba(195,169,255,0.5); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.touch-arrow { color: #C9A94E; font-size: 16px; }

/* Latest Mystic Drop preview card. */
.drop-card {
  display: flex; gap: 14px; text-decoration: none;
  padding: 12px; border-radius: 12px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  transition: border-color 0.2s, background-color 0.2s;
}
.drop-card:hover { border-color: rgba(201,169,78,0.5); background: rgba(255,255,255,0.06); }
.drop-img {
  width: 84px; height: 84px; flex-shrink: 0; border-radius: 10px;
  background-size: cover; background-position: center; background-color: #0e0820;
}
.drop-body { flex: 1; display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.drop-title { font-family: 'Cinzel', serif; font-size: 15px; color: #FFDFA3; }
.drop-teaser {
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 14px; color: rgba(232,216,255,0.85); line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.drop-cta { font-size: 11px; font-weight: 600; color: #C9A94E; margin-top: 2px; }

/* Recent shared readings list. */
.shared-list { display: flex; flex-direction: column; gap: 10px; }
.shared-row { display: flex; align-items: center; gap: 12px; }
.shared-thumb { width: 44px; height: 44px; border-radius: 8px; object-fit: cover; flex-shrink: 0; border: 1px solid rgba(255,255,255,0.1); }
.shared-thumb-empty { display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.04); font-size: 18px; }
.shared-info { flex: 1; min-width: 0; }
.shared-title { font-size: 13px; color: #E8D8FF; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.shared-meta { font-size: 11px; color: rgba(195,169,255,0.5); display: flex; gap: 8px; flex-wrap: wrap; margin-top: 2px; }

/* ── Phone (≤560px): the auto-fit stats grid and the row-layout drop card
   overflow a ~390px screen. Force the stats to two columns and stack the drop
   card's image above its body. Desktop layout above is untouched. ── */
@media (max-width: 560px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .drop-card { flex-direction: column; }
}

</style>
